import type { ChangeEvent } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, InputBaseProps, Typography as T } from "@mui/material";

import { Input, Label, LabelError, RequiredStar } from "components/atoms";

type Props = {
  labelText: string;
  labelErrorText?: string;
  required?: boolean;
  isError?: boolean;
  disabled?: boolean;
  name: string;
  additionalOnChange?: (
    event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  validateFunctionObject?: {
    [key: string]: (value: string) => boolean | string;
  };
} & InputBaseProps;

const InputBox = ({
  labelText,
  labelErrorText = "",
  required = true,
  isError = false,
  disabled = false,
  name,
  additionalOnChange,
  validateFunctionObject = {},
  ...props
}: Props) => {
  const { control } = useFormContext();

  const rulesObject = {
    validate: {
      ...validateFunctionObject,
      ...(required && {
        require111: (v: boolean) => !!v || "The field is required!",
      }),
    },
  };

  // eslint-disable-next-line no-constant-condition
  return name ? (
    <Controller
      control={control}
      name={name}
      shouldUnregister
      defaultValue=""
      rules={rulesObject}
      render={({
        field: { onChange, value, ref, name: fieldName },
        fieldState: { invalid, error },
      }) => (
        <>
          {labelText ? (
            <Label
              sx={{
                marginLeft: "2px",
                marginBottom: name === "couponCode" ? "10px" : 0,
                alignSelf: "flex-start",
              }}
            >
              <T variant="inputTitle">{labelText}</T>
              {required && <RequiredStar />}
            </Label>
          ) : null}
          <Input
            {...props}
            name={fieldName}
            inputRef={ref}
            disabled={disabled}
            onChange={(v) => {
              onChange(v);
              additionalOnChange?.(v);
            }}
            value={value}
            error={invalid}
          />
          <Box>
            {invalid && (
              <LabelError labelErrorText={error?.message || labelErrorText} />
            )}
          </Box>
        </>
      )}
    />
  ) : (
    <>
      {labelText ? (
        <Label
          sx={{
            marginBottom: 2,
          }}
        >
          <T variant="inputTitle">{labelText}</T>
          {required && <RequiredStar />}
        </Label>
      ) : null}
      <Input disabled={disabled} {...props} />
      <Box>{isError && <LabelError labelErrorText={labelErrorText} />}</Box>
    </>
  );
};

export default InputBox;
